import {
  getFieldsToComplete,
  getImagesToComplete,
  handleAiError,
  processCompletion,
  saveToElementMetadata,
  updateDocument,
  updateImages
} from "@/lib/ai/ai";
import { formatFieldsForOpenAI, getPrompt } from "@/lib/ai/prompts";
import { findNode } from "@/lib/tree/tree";
import { documentStore, globalStore } from "@/store/store";
import { ChatMessage, FieldToComplete } from "../ai/types";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import { requestCompletion } from "../ai/api";
import { getPromptInfoFromNode } from "./util";
import { TreeNode } from "@/types/tree";
import { breakdanceStoreActions } from "@/store/actions/store-actions";
import { OnMountedElement } from "@/types/store-actions";
import { SECTION_SLUG } from "../elements/constants";
import { aiJobController } from "@/types/singularity/ai-jobs";
import { callImageService } from "@/futurelayer/api/image-bridge";
import { replaceImagesInSectionWithAi } from "@/futurelayer/images/main";

export async function rewriteContentInSection(
  sectionId: TreeNode["id"],
  abortController: AbortController
) {
  const node = findNode(documentStore.document.tree, sectionId);

  if (!node) return;

  const promptInfo = getPromptInfoFromNode(node);

  const fields = getFieldsToComplete(node);
  const fieldsToFill = formatFieldsForOpenAI(fields);
  const prompt = getPrompt(
    fieldsToFill,
    promptInfo,
    globalStore.aiSettings.prompt
  );

  console.log(fields, fieldsToFill);

  if (!fields.length) return;

  try {
    const parsedCompletion = await syncRewrite(prompt, fields, abortController);
    updateDocument(parsedCompletion.elements);

    promptInfo.singularity.shouldGenerateOnInitialLoad = false;
    promptInfo.singularity.unsplashKeywords =
      parsedCompletion.unsplash_keywords;

    console.log(parsedCompletion);

    saveToElementMetadata(node, promptInfo);
  } catch (error) {
    handleAiError(error as string);
  }
}

export async function populateImagesInSection(
  sectionId: TreeNode["id"],
  _abortController: AbortController
) {
  alert("defunct, use replaceImagesInSectionWithAi instead");
  let x = 1;
  if (x === 1) {
    return;
  }

  const node = findNode(documentStore.document.tree, sectionId);

  if (!node) return;

  const promptInfo = getPromptInfoFromNode(node);

  console.log(promptInfo);

  const unsplashKeywords = promptInfo.singularity.unsplashKeywords.length
    ? promptInfo.singularity.unsplashKeywords
    : ["generic"];

  const imageFieldsToComplete = getImagesToComplete(node);
  if (!(imageFieldsToComplete.length > 0)) return;

  const response = await callImageService({
    query: unsplashKeywords.join(", "),
    page: 1,
    per_page: imageFieldsToComplete.length
  });

  updateImages(imageFieldsToComplete, response, false);
}

export async function syncRewrite(
  prompt: ChatMessage[],
  fields: FieldToComplete[],
  abortController: AbortController
) {
  const completion = pipe(
    await requestCompletion(
      prompt,
      "json",
      "breakdance_ai_request_completion",
      abortController
    ),
    fold(
      () => null,
      response => response
    )
  );

  if (!completion) {
    throw new Error("No response was returned from OpenAI API.");
  }

  const parsedCompletion = processCompletion(completion, fields);

  if (!parsedCompletion.elements.length) {
    throw new Error("No elements were returned from OpenAI API.");
  }

  return parsedCompletion;
}

export function registerStoreActionsForSingularitySectionGeneration() {
  if (window.Breakdance.isOnboarding) return;
  breakdanceStoreActions.register.onMountedElement((args: OnMountedElement) => {
    if (singularitySectionRewriteDuplicateAvoider.isProcessed(args.elementId)) {
      return;
    }

    const node = findNode(documentStore.document.tree, args.elementId);

    if (!node) return;

    if (node.data.type !== SECTION_SLUG) return;

    const promptInfo = getPromptInfoFromNode(node);

    if (promptInfo.singularity.shouldGenerateOnInitialLoad) {
      singularitySectionRewriteDuplicateAvoider.markAsProcessed(args.elementId);

      const section = findNode(documentStore.document.tree, node.id);
      if (!section) return;

      aiJobController.addJob(
        async abortController => {
          await rewriteContentInSection(node.id, abortController);
          await replaceImagesInSectionWithAi(section, abortController);
        },
        `rewrite and image population for shouldGenerateOnInitialLoad for document 
      "${documentStore.document.documentMeta.titleLabel} - ${documentStore.document.documentMeta.id}",
      section ${node.id}`,
        node.id
      );
    }
  });
}

// onMountedElement logic has a bug where it can be called multiple times for the same element
// This class is used to avoid processing the same section multiple times
class SingularitySectionRewriteDuplicateAvoider {
  private processedElementIds = new Set<TreeNode["id"]>();

  reset() {
    this.processedElementIds.clear();
  }

  isProcessed(elementId: TreeNode["id"]): boolean {
    return this.processedElementIds.has(elementId);
  }

  markAsProcessed(elementId: TreeNode["id"]): void {
    this.processedElementIds.add(elementId);
  }
}

const singularitySectionRewriteDuplicateAvoider =
  new SingularitySectionRewriteDuplicateAvoider();

export function resetSingularitySectionRewriteDuplicateAvoider() {
  singularitySectionRewriteDuplicateAvoider.reset();
}
